import { useFrame } from '@react-three/fiber';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Euler, Object3D, Vector3 } from 'three';

import Character from '@cartwhl/3d/r3f/components/Character';
import { inferMeshFormatFromPath } from '@cartwhl/3d/r3f/characterMeshFormat';
import useFrameMixer from '@cartwhl/3d/r3f/hooks/useFrameMixer';

import { usePlayerCharacterMeshOverrides } from '../hooks/playerCharacterMeshContext';
import type { RotationOffsets } from '@cartwhl/common/types';

import type { CharacterMeshFormat } from './AnimatedCharacter';

import { usePlayerSettingsContext } from '../hooks/playerSettingsContext';
import { useBVHAnimations } from '../hooks/useBVHAnimation';
import { usePlayerStoreApi } from '../store/playerStore';

export interface FallbackCharacterProps {
  playerId?: string;
  actorId?: string;
  characterID: string;
  meshPath: string;
  meshFormat?: CharacterMeshFormat;
  bvhUrls: string[];
  shuffle?: boolean;
  rotationOffsets?: RotationOffsets;
  rotation?: Euler;
  position?: Vector3;
  skinHex?: string;
  faceExpression?: string;
  onSceneReady?: (scene: Object3D) => void;
  onCharacterLoading?: (loading: boolean) => void;
  headBoneName?: string | null;
}

export default function FallbackCharacter({
  playerId,
  actorId,
  characterID,
  meshPath,
  meshFormat,
  bvhUrls,
  shuffle = false,
  rotationOffsets,
  rotation,
  position,
  skinHex,
  faceExpression,
  onSceneReady,
  onCharacterLoading,
  headBoneName,
}: FallbackCharacterProps) {
  const store = usePlayerStoreApi();
  const characterMeshOverrides = usePlayerCharacterMeshOverrides();
  const resolvedMeshFormat = useMemo(
    () => meshFormat ?? inferMeshFormatFromPath(meshPath),
    [meshFormat, meshPath],
  );
  const resolvedLoadFBX = characterMeshOverrides?.loadFBX;
  const resolvedTransformLoadedMesh = useMemo(() => {
    if (resolvedMeshFormat !== 'fbx') return undefined;
    return characterMeshOverrides?.createTransformLoadedMesh?.(meshPath);
  }, [characterMeshOverrides, meshPath, resolvedMeshFormat]);
  const [characterScene, setCharacterScene] = useState<Object3D | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const resolvedBvhUrls = useMemo(() => {
    if (!shuffle) {
      return bvhUrls;
    }

    const shuffledUrls = [...bvhUrls];
    shuffledUrls.forEach((_, index, urls) => {
      const randomIndex = Math.floor(Math.random() * urls.length);
      [urls[index], urls[randomIndex]] = [urls[randomIndex], urls[index]];
    });

    return shuffledUrls;
  }, [bvhUrls, shuffle]);
  const resolvedBvhUrlsKey = resolvedBvhUrls.join('|');
  const {
    settings: { character },
  } = usePlayerSettingsContext();
  const {
    bvhs: fallbackAnimations,
    isLoading: isAnimationLoading,
    error: animationError,
  } = useBVHAnimations({
    bvhUrls: resolvedBvhUrls,
    rotationOffsets,
  });
  const resolvedActorId = actorId ?? playerId ?? characterID;

  const activeAnimation =
    fallbackAnimations[activeIndex] ?? fallbackAnimations[0] ?? null;
  const mixer = useFrameMixer(
    activeAnimation?.clip,
    characterScene ?? undefined,
    {
      frameRate: 60,
      autoplay: true,
      loop: true,
      onLoopFinish: () => {
        if (fallbackAnimations.length > 1) {
          setActiveIndex((currentIndex) => {
            return (currentIndex + 1) % fallbackAnimations.length;
          });
        }
      },
    },
  );

  useEffect(() => {
    store.registerActor(resolvedActorId);
    return () => {
      store.unregisterActor(resolvedActorId);
    };
  }, [resolvedActorId, store]);

  useEffect(() => {
    store.setActorHeadBoneName(resolvedActorId, headBoneName);
  }, [headBoneName, resolvedActorId, store]);

  useEffect(() => {
    store.setActorMeshStatus(resolvedActorId, 'initial');
  }, [meshPath, resolvedActorId, store]);

  useEffect(() => {
    if (!characterScene) {
      return;
    }

    store.registerActorScene(resolvedActorId, characterScene);
    return () => {
      store.unregisterActorScene(resolvedActorId);
    };
  }, [characterScene, resolvedActorId, store]);

  useEffect(() => {
    store.setActorAnimationStatus(resolvedActorId, 'initial');
    setActiveIndex(0);
  }, [resolvedActorId, resolvedBvhUrlsKey, store]);

  useEffect(() => {
    if (resolvedBvhUrls.length === 0) {
      store.setActorAnimationStatus(resolvedActorId, 'initial');
      store.setActorAssetError(resolvedActorId, null);
      return;
    }

    if (isAnimationLoading) {
      store.setActorAnimationStatus(resolvedActorId, 'pending');
      return;
    }

    store.setActorAnimationStatus(
      resolvedActorId,
      fallbackAnimations.length > 0 ? 'ready' : 'initial',
    );
    store.setActorAssetError(resolvedActorId, animationError);
  }, [
    animationError,
    fallbackAnimations.length,
    isAnimationLoading,
    resolvedActorId,
    resolvedBvhUrls.length,
    resolvedBvhUrlsKey,
    store,
  ]);

  const handleSceneReady = useCallback(
    (scene: Object3D) => {
      store.setActorMeshStatus(resolvedActorId, 'ready');
      setCharacterScene((currentScene) =>
        currentScene === scene ? currentScene : scene,
      );
      onSceneReady?.(scene);
    },
    [onSceneReady, resolvedActorId, store],
  );

  const handleCharacterLoading = (loading: boolean) => {
    store.setActorMeshStatus(resolvedActorId, loading ? 'pending' : 'ready');
    onCharacterLoading?.(loading);
  };

  useFrame((_, delta) => {
    mixer.update(delta);
  }, -1);

  return (
    <Character
      characterID={characterID}
      meshPath={meshPath}
      meshFormat={resolvedMeshFormat}
      loadFBX={resolvedLoadFBX}
      transformLoadedMesh={resolvedTransformLoadedMesh}
      rotation={rotation}
      position={position}
      skinHex={skinHex ?? character.skinHex}
      faceExpression={faceExpression ?? character.faceExpression}
      showSkeleton={character.showSkeleton}
      onSceneReady={handleSceneReady}
      onCharacterLoading={handleCharacterLoading}
    />
  );
}
